import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, message, Layout, Checkbox, Tag } from 'antd';
import { UserOutlined, LockOutlined, GoogleOutlined, ShopOutlined } from '@ant-design/icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { authAPI } from '../services/api';
import Brand from '../components/Brand';
import styles from './Login.module.css';

const { Content } = Layout;

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [resuming, setResuming] = useState<number | null>(null);
  const { login, isAuthenticated, role, sessions } = useAuth();
  const navigate = useNavigate();
  const { showErrorModal } = useErrorHandler();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Set when the master-admin deep-links here to enter a specific store.
  const org = searchParams.get('org') || undefined;
  // «Додати акаунт» mode: the header menu deep-links here; an already-authed
  // user must see the form instead of being bounced to the dashboard.
  const addMode = searchParams.get('add') === '1';

  const defaultRoute = role === 'ai_agent' ? '/products' : '/dashboard';
  // A guarded route sends people here with where they were heading. Resume that
  // instead of the role default, or a flow started elsewhere — approving an MCP
  // connection, say — ends silently on the dashboard.
  const attempted = (location.state as { from?: { pathname: string; search?: string } } | null)?.from;
  const target = attempted ? `${attempted.pathname}${attempted.search || ''}` : defaultRoute;

  useEffect(() => {
    if (isAuthenticated && !addMode) {
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, navigate, target, addMode]);

  const onFinish = async (values: { username: string; password: string; remember?: boolean }) => {
    setLoading(true);
    try {
      await login(values.username, values.password, values.remember, org);
      message.success('Успішний вхід!');
      if (addMode) {
        // Full reload into the new account so nothing cached from the
        // previous account's tenant survives.
        window.location.assign('/');
        return;
      }
    } catch (error: any) {
      const errorDetails = {
        response: {
          status: error?.response?.status,
          data: {
            detail: error?.response?.status === 401
              ? 'Невірне імʼя користувача або пароль. Перевірте правильність введених даних.'
              : error?.response?.data?.detail || 'Не вдалося виконати вхід. Спробуйте пізніше.',
          },
        },
        message: error?.message,
      };
      showErrorModal(errorDetails, 'Помилка входу');
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async (memberId: number) => {
    setResuming(memberId);
    try {
      const ok = await authAPI.resumeSession(memberId);
      if (ok) {
        window.location.assign('/');
        return;
      }
      const dead = sessions.find((s) => s.user.id === memberId);
      if (dead) form.setFieldsValue({ username: dead.user.username });
      message.info('Сесія закінчилась — введіть пароль ще раз.');
    } finally {
      setResuming(null);
    }
  };

  const handleGoogle = () => {
    // Start the backend Google OAuth flow (admin audience): redirects to Google
    // and back to the callback, which issues the admin session.
    const next = encodeURIComponent(window.location.origin + '/dashboard');
    window.location.href = `/api/v1/admin/auth/google/login?next=${next}`;
  };

  return (
    <Layout className={styles.layout}>
      <Content className={styles.content}>
        <Card className={styles.card} bordered={false} bodyStyle={{ padding: '36px 32px' }}>
          <div className={styles.header}>
            <div className={styles.brandWrap}>
              <Brand variant="full" context="light" />
            </div>
            <p className={styles.subtitle}>Вхід до панелі управління</p>
            {org && (
              <Tag icon={<ShopOutlined />} color="blue" style={{ marginTop: 8 }}>
                Магазин: {org}
              </Tag>
            )}
          </div>

          {sessions.length > 0 && !addMode && (
            <div style={{ marginBottom: 16 }}>
              {sessions.map((s) => (
                <Button
                  key={s.user.id}
                  block
                  size="large"
                  icon={<UserOutlined />}
                  loading={resuming === s.user.id}
                  disabled={resuming !== null && resuming !== s.user.id}
                  onClick={() => handleResume(s.user.id)}
                  style={{ marginBottom: 8, textAlign: 'left' }}
                >
                  {s.user.username}
                  {s.orgName ? ` — ${s.orgName}` : ''}
                </Button>
              ))}
            </div>
          )}

          <Button
            block
            size="large"
            icon={<GoogleOutlined />}
            className={styles.googleButton}
            onClick={handleGoogle}
          >
            Продовжити з Google
          </Button>

          <div className={styles.divider}><span>або</span></div>

          <Form form={form} name="login" initialValues={{ remember: true }} onFinish={onFinish} size="large">
            <Form.Item
              name="username"
              rules={[{ required: true, message: 'Будь ласка, введіть імʼя користувача!' }]}
            >
              <Input prefix={<UserOutlined />} placeholder="Імʼя користувача" />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Будь ласка, введіть пароль!' }]}
            >
              <Input.Password prefix={<LockOutlined />} placeholder="Пароль" />
            </Form.Item>
            <Form.Item name="remember" valuePropName="checked" className={styles.rememberItem}>
              <Checkbox>Запамʼятати мене</Checkbox>
            </Form.Item>
            <Form.Item className={styles.submitItem}>
              <Button type="primary" htmlType="submit" block loading={loading} className={styles.submitButton}>
                Увійти
              </Button>
            </Form.Item>
          </Form>

          <p className={styles.footnote}>Spot Engine — платформа для вашого магазину</p>
        </Card>
      </Content>
    </Layout>
  );
};

export default Login;
